// @ts-ignore -- Vite/bundler raw fixture import is the browser-safe canonical shape source.
import canonicalShapesTtl from "../fixtures/design-binding-shapes-v1.ttl?raw"
// @ts-ignore -- n3 does not publish declarations compatible with this workspace.
import { Parser as N3Parser } from "n3"
// @ts-ignore -- rdf-ext does not publish declarations compatible with this workspace.
import rdf from "rdf-ext"
// @ts-ignore -- rdf-validate-shacl does not publish declarations compatible with this workspace.
import SHACLValidator from "rdf-validate-shacl"
import type { Quad } from "@rdfjs/types"

export type DesignBindingQuad = Quad

/**
 * The fail-closed Design Ontology binding vocabulary. Mirrors
 * `DesignOntologyBindingGapCode` in @cogo/agentic-generator
 * src/design-ontology-binding/types.ts; the L1 shard must declare all four.
 */
export const DESIGN_BINDING_GAP_CODES = [
  "design-ontology-release-ref-mismatch",
  "design-ontology-release-ref-missing",
  "design-ontology-release-not-found",
  "design-ontology-release-resolution-failed",
] as const

export type DesignBindingGapCode = (typeof DESIGN_BINDING_GAP_CODES)[number]

export interface DesignBindingShaclIssue {
  code: "shacl-conformance-failed"
  path: string
  message: string
  focusNode?: string
}

export interface DesignBindingCardinalityIssue {
  code: "binding-cardinality-invalid"
  applicationRefs: string[]
  message: string
}

export interface DesignBindingDuplicateApplicationRefIssue {
  code: "binding-duplicate-application-ref"
  applicationRef: string
  artifactNodes: string[]
  message: string
}

export interface DesignBindingReleaseUnresolvableIssue {
  code: "binding-release-unresolvable"
  applicationRef: string
  releaseNode: string
  message: string
}

export interface DesignBindingRefMismatchIssue {
  code: "binding-ref-mismatch"
  applicationRef: string
  declaredRef: string
  resolvedReleaseId: string
  message: string
}

export interface DesignBindingTokenNotAdmittedIssue {
  code: "binding-token-not-admitted"
  applicationRef: string
  tokenRef: string
  message: string
}

export interface DesignBindingTokenNotCanonicalIssue {
  code: "binding-token-not-canonical"
  applicationRef: string
  tokenRef: string
  message: string
}

export interface DesignBindingGapVocabularyIssue {
  code: "binding-gap-vocabulary-incomplete"
  missingGapCodes: string[]
  message: string
}

export interface DesignBindingRdfInputIssue {
  code: "rdf-input-invalid"
  path: string
  message: string
}

export type DesignBindingAdmissionIssue =
  | DesignBindingShaclIssue
  | DesignBindingCardinalityIssue
  | DesignBindingDuplicateApplicationRefIssue
  | DesignBindingReleaseUnresolvableIssue
  | DesignBindingRefMismatchIssue
  | DesignBindingTokenNotAdmittedIssue
  | DesignBindingTokenNotCanonicalIssue
  | DesignBindingGapVocabularyIssue
  | DesignBindingRdfInputIssue

export interface DesignBindingAdmissionResult {
  ok: boolean
  issues: DesignBindingAdmissionIssue[]
}

const RDF_TYPE = "http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
const DESIGN_BINDING_NS = "https://cogo.xyz/ns/design-binding/v1#"
const DESIGN_COMPOSITION_NS = "https://cogo.xyz/ns/design-composition/v1#"
const COGO_NS = "https://cogo.xyz/ns/v1#"
const TOKEN_INSTANCE_NS = "https://cogo.xyz/instances/token/"
const TOKEN_REF_PREFIX = "ctok:"

const APPLICATION_ARTIFACT = `${DESIGN_BINDING_NS}ApplicationArtifact`
const APPLICATION_REF = `${DESIGN_BINDING_NS}applicationRef`
const BINDING_GAP = `${DESIGN_BINDING_NS}DesignOntologyBindingGap`
const GAP_CODE = `${DESIGN_BINDING_NS}gapCode`
const DESIGN_ONTOLOGY_RELEASE_REF = `${DESIGN_BINDING_NS}designOntologyReleaseRef`
const BINDS_RELEASE = `${DESIGN_BINDING_NS}bindsRelease`
const REQUIRED_TOKEN_REF = `${DESIGN_BINDING_NS}requiredTokenRef`
const RELEASE_ID = `${DESIGN_COMPOSITION_NS}releaseId`
const RELEASE_TOKEN_REF = `${DESIGN_COMPOSITION_NS}tokenRef`
const COGO_TOKEN = `${COGO_NS}Token`

function parseDataset(ttl: string) {
  const dataset = rdf.dataset()
  for (const quad of new N3Parser().parse(ttl)) dataset.add(quad)
  return dataset
}

function shaclMessage(result: any): string {
  const messages = Array.isArray(result?.message)
    ? result.message
    : result?.message
      ? [result.message]
      : []
  const rendered = messages
    .map((candidate: any) => candidate?.value ?? String(candidate))
    .filter(Boolean)
  return rendered.join("; ") || "Design Ontology binding SHACL constraint failed."
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function hasRdfTermBase(
  value: unknown,
  allowedTermTypes: readonly string[],
): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    typeof value.termType === "string" &&
    allowedTermTypes.includes(value.termType) &&
    typeof value.value === "string" &&
    typeof value.equals === "function"
  )
}

function isRdfJsQuad(value: unknown): value is Quad {
  if (!hasRdfTermBase(value, ["Quad"]) || value.value !== "") return false
  const subject = value.subject
  const predicate = value.predicate
  const object = value.object
  const graph = value.graph

  const subjectValid =
    hasRdfTermBase(subject, ["NamedNode", "BlankNode", "Variable"]) ||
    isRdfJsQuad(subject)
  const predicateValid = hasRdfTermBase(predicate, ["NamedNode", "Variable"])
  const objectValid =
    hasRdfTermBase(object, [
      "NamedNode",
      "BlankNode",
      "Literal",
      "Variable",
    ]) || isRdfJsQuad(object)
  const graphValid = hasRdfTermBase(graph, [
    "DefaultGraph",
    "NamedNode",
    "BlankNode",
    "Variable",
  ])

  if (!subjectValid || !predicateValid || !objectValid || !graphValid) {
    return false
  }
  if (
    isRecord(graph) &&
    graph.termType === "DefaultGraph" &&
    graph.value !== ""
  ) {
    return false
  }
  if (isRecord(object) && object.termType === "Literal") {
    return (
      typeof object.language === "string" &&
      hasRdfTermBase(object.datatype, ["NamedNode"])
    )
  }
  return true
}

function rdfInputFailure(
  path: string,
  message: string,
): DesignBindingAdmissionResult {
  return { ok: false, issues: [{ code: "rdf-input-invalid", path, message }] }
}

interface BindingIndex {
  applicationRefs: string[]
  applicationRefLiteralByArtifact: Map<string, Set<string>>
  releaseRefByApplication: Map<string, Set<string>>
  declaredRefByApplication: Map<string, Set<string>>
  requiredTokensByApplication: Map<string, Set<string>>
  releaseIdByNode: Map<string, Set<string>>
  releaseTokensByNode: Map<string, Set<string>>
  canonicalTokenIris: Set<string>
  declaredGapCodes: Set<string>
}

function indexQuads(quads: readonly DesignBindingQuad[]): BindingIndex {
  const applicationRefs = new Set<string>()
  const gapNodes = new Set<string>()
  const releaseRefByApplication = new Map<string, Set<string>>()
  const declaredRefByApplication = new Map<string, Set<string>>()
  const requiredTokensByApplication = new Map<string, Set<string>>()
  const applicationRefLiteralByArtifact = new Map<string, Set<string>>()
  const releaseIdByNode = new Map<string, Set<string>>()
  const releaseTokensByNode = new Map<string, Set<string>>()
  const canonicalTokenIris = new Set<string>()
  const gapCodeByNode = new Map<string, Set<string>>()

  const push = (
    map: Map<string, Set<string>>,
    key: string,
    value: string,
  ): void => {
    const bucket = map.get(key) ?? new Set<string>()
    bucket.add(value)
    map.set(key, bucket)
  }

  for (const quad of quads) {
    const subject = quad.subject.value
    const predicate = quad.predicate.value
    const object = quad.object.value

    if (predicate === RDF_TYPE) {
      if (object === APPLICATION_ARTIFACT) applicationRefs.add(subject)
      if (object === BINDING_GAP) gapNodes.add(subject)
      if (object === COGO_TOKEN) canonicalTokenIris.add(subject)
      continue
    }
    if (predicate === BINDS_RELEASE) push(releaseRefByApplication, subject, object)
    if (predicate === DESIGN_ONTOLOGY_RELEASE_REF) {
      push(declaredRefByApplication, subject, object)
    }
    if (predicate === REQUIRED_TOKEN_REF) {
      push(requiredTokensByApplication, subject, object)
    }
    if (predicate === APPLICATION_REF) {
      push(applicationRefLiteralByArtifact, subject, object)
    }
    if (predicate === RELEASE_ID) push(releaseIdByNode, subject, object)
    if (predicate === RELEASE_TOKEN_REF) push(releaseTokensByNode, subject, object)
    if (predicate === GAP_CODE) push(gapCodeByNode, subject, object)
  }

  const declaredGapCodes = new Set<string>()
  for (const gapNode of gapNodes) {
    for (const code of gapCodeByNode.get(gapNode) ?? []) {
      declaredGapCodes.add(code)
    }
  }

  return {
    applicationRefs: [...applicationRefs].sort(),
    applicationRefLiteralByArtifact,
    releaseRefByApplication,
    declaredRefByApplication,
    requiredTokensByApplication,
    releaseIdByNode,
    releaseTokensByNode,
    canonicalTokenIris,
    declaredGapCodes,
  }
}

function bindingIssues(index: BindingIndex): DesignBindingAdmissionIssue[] {
  const issues: DesignBindingAdmissionIssue[] = []

  for (const applicationRef of index.applicationRefs) {
    const declaredRefs = [
      ...(index.declaredRefByApplication.get(applicationRef) ?? []),
    ].sort()
    const releaseNodes = [
      ...(index.releaseRefByApplication.get(applicationRef) ?? []),
    ].sort()

    for (const releaseNode of releaseNodes) {
      const releaseIds = [
        ...(index.releaseIdByNode.get(releaseNode) ?? []),
      ].sort()
      if (releaseIds.length !== 1) {
        issues.push({
          code: "binding-release-unresolvable",
          applicationRef,
          releaseNode,
          message:
            "The Design Ontology binding target does not resolve to exactly one design composition release id.",
        })
        continue
      }
      const [resolvedReleaseId] = releaseIds
      for (const declaredRef of declaredRefs) {
        if (declaredRef === resolvedReleaseId) continue
        issues.push({
          code: "binding-ref-mismatch",
          applicationRef,
          declaredRef,
          resolvedReleaseId,
          message:
            "The declared Design Ontology release reference does not match the resolved release identity.",
        })
      }

      const admittedTokens = index.releaseTokensByNode.get(releaseNode) ?? new Set()
      for (const tokenRef of [
        ...(index.requiredTokensByApplication.get(applicationRef) ?? []),
      ].sort()) {
        if (!admittedTokens.has(tokenRef)) {
          issues.push({
            code: "binding-token-not-admitted",
            applicationRef,
            tokenRef,
            message:
              "The bound Design Ontology release does not admit a required token ref.",
          })
        }
      }
    }

    for (const tokenRef of [
      ...(index.requiredTokensByApplication.get(applicationRef) ?? []),
    ].sort()) {
      if (!tokenRef.startsWith(TOKEN_REF_PREFIX)) continue
      const tokenIri = `${TOKEN_INSTANCE_NS}${tokenRef.slice(TOKEN_REF_PREFIX.length)}`
      if (index.canonicalTokenIris.has(tokenIri)) continue
      issues.push({
        code: "binding-token-not-canonical",
        applicationRef,
        tokenRef,
        message:
          "A required token ref is not declared as a canonical cogo:Token in the admitted token registry.",
      })
    }
  }

  return issues
}

/**
 * Sole public Design Ontology binding admission gate. It always evaluates the
 * canonical SHACL shapes, requires at least one bound application artifact with
 * a unique application identity, and then applies the deterministic binding
 * rules SHACL Core cannot express: binding-target resolution, declared-ref ↔
 * release-id agreement, required token admission by the bound release, required
 * token canonicality against the `cogo:Token` registry, and fail-closed
 * gap-vocabulary completeness.
 *
 * The token registry (tokens-v1) is NOT implied — callers must union it into
 * the input graph, otherwise every required token is reported as non-canonical.
 */
export async function admitDesignBindingOntology(
  quads: Iterable<DesignBindingQuad>,
): Promise<DesignBindingAdmissionResult> {
  let materialized: DesignBindingQuad[]
  try {
    if (
      quads === null ||
      quads === undefined ||
      typeof (quads as any)[Symbol.iterator] !== "function"
    ) {
      return rdfInputFailure(
        "quads",
        "Design Ontology binding RDF input must be an iterable of RDFJS Quads.",
      )
    }
    materialized = [...quads]
  } catch (error) {
    return rdfInputFailure(
      "quads",
      `Design Ontology binding RDF input could not be read: ${(error as Error).message}`,
    )
  }

  try {
    const invalidIndex = materialized.findIndex((quad) => !isRdfJsQuad(quad))
    if (invalidIndex >= 0) {
      return rdfInputFailure(
        `quads[${invalidIndex}]`,
        "Design Ontology binding RDF input must contain complete RDFJS Quad values.",
      )
    }
  } catch (error) {
    return rdfInputFailure(
      "quads",
      `Design Ontology binding RDF input validation failed: ${(error as Error).message}`,
    )
  }

  const dataset = rdf.dataset()
  try {
    for (const quad of materialized) dataset.add(quad)
  } catch (error) {
    return rdfInputFailure(
      "quads",
      `Design Ontology binding RDF input could not be loaded: ${(error as Error).message}`,
    )
  }

  const validator = new SHACLValidator(parseDataset(canonicalShapesTtl), {
    importGraph: async () => rdf.dataset(),
  })
  const report = await validator.validate(dataset)
  const shaclIssues: DesignBindingShaclIssue[] = (report.results ?? []).map(
    (result: any) => ({
      code: "shacl-conformance-failed",
      path: result.path?.value ?? "",
      message: shaclMessage(result),
      ...(result.focusNode?.value ? { focusNode: result.focusNode.value } : {}),
    }),
  )

  const index = indexQuads(materialized)
  // Anti-vacuity guard: an empty or artifact-less graph must never be admitted
  // as "nothing to check". The Library is a MULTI-application corpus (L2 adds
  // the newsletter designation next to mobile-field-task), so the guard is
  // "at least one", and identity uniqueness is enforced separately below —
  // every per-artifact rule already runs per artifact, so nothing is relaxed.
  const cardinalityIssues: DesignBindingCardinalityIssue[] =
    index.applicationRefs.length >= 1
      ? []
      : [
          {
            code: "binding-cardinality-invalid",
            applicationRefs: index.applicationRefs,
            message: `At least one design-bound application artifact is required; received ${index.applicationRefs.length}.`,
          },
        ]

  // Two artifacts claiming the same application identity would make the
  // binding lookup ambiguous, so it is a rejection rather than a last-wins.
  const artifactsByApplicationRef = new Map<string, string[]>()
  for (const artifactNode of index.applicationRefs) {
    for (const applicationRef of index.applicationRefLiteralByArtifact.get(
      artifactNode,
    ) ?? []) {
      artifactsByApplicationRef.set(applicationRef, [
        ...(artifactsByApplicationRef.get(applicationRef) ?? []),
        artifactNode,
      ])
    }
  }
  const duplicateApplicationRefIssues: DesignBindingDuplicateApplicationRefIssue[] =
    [...artifactsByApplicationRef]
      .filter(([, artifactNodes]) => artifactNodes.length > 1)
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
      .map(([applicationRef, artifactNodes]) => ({
        code: "binding-duplicate-application-ref",
        applicationRef,
        artifactNodes: [...artifactNodes].sort(),
        message:
          "An application reference is bound by more than one design-bound application artifact.",
      }))

  const missingGapCodes = DESIGN_BINDING_GAP_CODES.filter(
    (code) => !index.declaredGapCodes.has(code),
  )
  const gapVocabularyIssues: DesignBindingGapVocabularyIssue[] =
    missingGapCodes.length === 0
      ? []
      : [
          {
            code: "binding-gap-vocabulary-incomplete",
            missingGapCodes: [...missingGapCodes],
            message:
              "The fail-closed Design Ontology binding gap vocabulary is incomplete.",
          },
        ]

  const issues: DesignBindingAdmissionIssue[] = [
    ...shaclIssues,
    ...cardinalityIssues,
    ...duplicateApplicationRefIssues,
    ...gapVocabularyIssues,
    ...bindingIssues(index),
  ]
  return { ok: issues.length === 0, issues }
}
